import { ObjectId } from 'bson';
import { useTranslation } from 'react-i18next';
import { useFinancingOfferLenderContactsApi } from '@hero/api-hooks';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Spinner,
} from '@hero/web-components';
import { MailIcon, PhoneIcon, SupportIcon } from '@hero/web-icons';
import type { OfferMock } from '../mocks';

export type ContactLenderDialogProps = {
  applicationId: string;
  /** Offer row to resolve the lender behind. `null` keeps the dialog closed. */
  offer: OfferMock | null;
  lenderName: string;
  onClose: () => void;
};

/**
 * Modal listing the lender's opted-in contacts (`receiveNewApplicationEvent`)
 * for a specific offer row. Fetches on open so we never leak contacts for
 * offers the carrier hasn't expanded.
 */
export function ContactLenderDialog({
  applicationId,
  offer,
  lenderName,
  onClose,
}: ContactLenderDialogProps) {
  const { t } = useTranslation('m-financing');

  const contactsQuery = useFinancingOfferLenderContactsApi(
    offer
      ? {
          _id: new ObjectId(applicationId),
          offerId: offer.id,
        }
      : undefined,
    { enabled: offer !== null },
  );

  const contacts = contactsQuery.data?.contacts ?? [];

  return (
    <Dialog
      open={offer !== null}
      onOpenChange={open => {
        if (!open) onClose();
      }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SupportIcon className="size-5 shrink-0 text-gray-500" />
            {t('offer_card.contact.modal_title', { lender: lenderName })}
          </DialogTitle>
          <DialogDescription>
            {t('offer_card.contact.modal_description')}
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          {contactsQuery.isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner className="size-6 text-gray-400" />
            </div>
          ) : contacts.length === 0 ? (
            <p className="text-sm text-gray-600">
              {t('offer_card.contact.empty')}
            </p>
          ) : (
            <ul className="space-y-3">
              {contacts.map((contact, index) => (
                <li
                  key={`${contact.email}-${index}`}
                  className="rounded-xl border border-gray-200 bg-white p-3">
                  <p className="text-sm font-medium text-gray-900">
                    {contact.name}
                  </p>
                  <div className="mt-2 flex flex-col gap-1.5">
                    {contact.email && (
                      <a
                        href={`mailto:${contact.email}`}
                        className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-900">
                        <MailIcon className="size-3.5 shrink-0 text-gray-400" />
                        <span>{contact.email}</span>
                      </a>
                    )}
                    {contact.phone && (
                      <a
                        href={`tel:${contact.phone}`}
                        className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-900"
                        dir="ltr">
                        <PhoneIcon className="size-3.5 shrink-0 text-gray-400" />
                        <span>{contact.phone}</span>
                      </a>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
